import { Product } from '../types';

// Load products data
let productsData: Product[] = [];

// Products that must show an exact price instead of the rounded one.
const exactPrices: Record<string, number> = {
  'baby-0004': 310
};

const normalizePrice = (price: number): number => {
  const ceiled = Math.ceil(price / 100) * 100;
  if (ceiled < 100) return 100;
  if (ceiled > 3000) return 3000;
  return ceiled;
};

// Function to load products
export const loadProducts = async (): Promise<Product[]> => {
  if (productsData.length === 0) {
    try {
      const response = await fetch('/products.json');
      const raw: Product[] = await response.json();
      productsData = raw.map(p => ({ ...p, price: exactPrices[p.id] ?? normalizePrice(p.price) }));
    } catch (error) {
      console.error('Error loading products:', error);
      productsData = [];
    }
  }
  return productsData;
};

// Get products (will load if not already loaded)
export const getProducts = async (): Promise<Product[]> => {
  return await loadProducts();
};

// For backward compatibility, export an empty array initially
export const products: Product[] = [];

// Helper functions for product data
export const getProductById = async (id: string): Promise<Product | undefined> => {
  const productsData = await getProducts();
  return productsData.find(product => product.id === id);
};

export const getProductsByCategory = async (category: string): Promise<Product[]> => {
  const productsData = await getProducts();
  return productsData.filter(product => 
    product.category.toLowerCase() === category.toLowerCase()
  );
};

export const getFeaturedProducts = async (limit: number = 8): Promise<Product[]> => {
  const productsData = await getProducts();
  // Prioritize new and sale products, but include others if needed
  const newAndSaleProducts = productsData.filter(product => product.isNew || product.isSale);
  if (newAndSaleProducts.length >= limit) {
    return newAndSaleProducts.slice(0, limit);
  }
  // If not enough new/sale products, add regular products to fill the limit
  const regularProducts = productsData.filter(product => !product.isNew && !product.isSale);
  return [...newAndSaleProducts, ...regularProducts].slice(0, limit);
};

export const getNewProducts = async (limit: number = 8): Promise<Product[]> => {
  const productsData = await getProducts();
  const newProducts = productsData.filter(product => product.isNew);
  if (newProducts.length >= limit) {
    return newProducts.slice(0, limit);
  }
  // If not enough new products, add some regular products to fill the limit
  const regularProducts = productsData.filter(product => !product.isNew && !product.isSale);
  return [...newProducts, ...regularProducts].slice(0, limit);
};

export const getSaleProducts = async (limit: number = 8): Promise<Product[]> => {
  const productsData = await getProducts();
  const saleProducts = productsData.filter(product => product.isSale);
  if (saleProducts.length >= limit) {
    return saleProducts.slice(0, limit);
  }
  // If not enough sale products, add some regular products to fill the limit
  const regularProducts = productsData.filter(product => !product.isNew && !product.isSale);
  return [...saleProducts, ...regularProducts].slice(0, limit);
};

export const searchProducts = async (query: string): Promise<Product[]> => {
  const productsData = await getProducts();
  const lowercaseQuery = query.toLowerCase();
  return productsData.filter(product =>
    product.name.toLowerCase().includes(lowercaseQuery) ||
    product.category.toLowerCase().includes(lowercaseQuery) ||
    product.model.toLowerCase().includes(lowercaseQuery) ||
    product.color.toLowerCase().includes(lowercaseQuery) ||
    product.printName.toLowerCase().includes(lowercaseQuery)
  );
};

export const getCategories = async (): Promise<string[]> => {
  const productsData = await getProducts();
  const categories = new Set(productsData.map(product => product.category));
  return Array.from(categories).sort();
};

export const getColors = async (): Promise<string[]> => {
  const productsData = await getProducts();
  const colors = new Set(productsData.map(product => product.color));
  return Array.from(colors).sort();
};

export const getSizes = async (): Promise<string[]> => {
  const productsData = await getProducts();
  const sizes = new Set(productsData.flatMap(product => product.sizes));
  return Array.from(sizes).sort();
};

export const getPriceRange = async (): Promise<[number, number]> => {
  const productsData = await getProducts();
  const prices = productsData.map(product => product.price);
  return [Math.min(...prices), Math.max(...prices)];
};

export const filterProducts = (products: Product[], filters: {
  category?: string;
  priceRange?: [number, number];
  sizes?: string[];
  colors?: string[];
  isNew?: boolean;
  isSale?: boolean;
  search?: string;
}): Product[] => {
  let filteredProducts = [...products];

  if (filters.search) {
    const lowercaseQuery = filters.search.toLowerCase();
    filteredProducts = filteredProducts.filter(product =>
      product.name.toLowerCase().includes(lowercaseQuery) ||
      product.category.toLowerCase().includes(lowercaseQuery) ||
      product.model.toLowerCase().includes(lowercaseQuery) ||
      product.color.toLowerCase().includes(lowercaseQuery) ||
      product.printName.toLowerCase().includes(lowercaseQuery)
    );
  }

  if (filters.category) {
    filteredProducts = filteredProducts.filter(product =>
      product.category.toLowerCase() === filters.category!.toLowerCase()
    );
  }

  if (filters.priceRange) {
    const [min, max] = filters.priceRange;
    filteredProducts = filteredProducts.filter(product =>
      product.price >= min && product.price <= max
    );
  }

  if (filters.sizes && filters.sizes.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      product.sizes.some(size => filters.sizes!.includes(size))
    );
  }

  if (filters.colors && filters.colors.length > 0) {
    filteredProducts = filteredProducts.filter(product =>
      filters.colors!.includes(product.color)
    );
  }

  if (filters.isNew !== undefined) {
    filteredProducts = filteredProducts.filter(product =>
      product.isNew === filters.isNew
    );
  }

  if (filters.isSale !== undefined) {
    filteredProducts = filteredProducts.filter(product =>
      product.isSale === filters.isSale
    );
  }

  return filteredProducts;
};

export const sortProducts = (products: Product[], sortBy: string): Product[] => {
  const sortedProducts = [...products];

  switch (sortBy) {
    case 'price-low-high':
      return sortedProducts.sort((a, b) => a.price - b.price);
    case 'price-high-low':
      return sortedProducts.sort((a, b) => b.price - a.price);
    case 'name-a-z':
      return sortedProducts.sort((a, b) => a.name.localeCompare(b.name));
    case 'name-z-a':
      return sortedProducts.sort((a, b) => b.name.localeCompare(a.name));
    case 'newest':
      return sortedProducts.sort((a, b) => {
        if (a.isNew && !b.isNew) return -1;
        if (!a.isNew && b.isNew) return 1;
        return 0;
      });
    case 'popular':
      // For now, we'll use a simple popularity based on isNew and isSale
      return sortedProducts.sort((a, b) => {
        const aScore = (a.isNew ? 2 : 0) + (a.isSale ? 1 : 0);
        const bScore = (b.isNew ? 2 : 0) + (b.isSale ? 1 : 0);
        return bScore - aScore;
      });
    default:
      return sortedProducts;
  }
};
